// client/src/components/pages/TelegramBI/ScheduleManager/index.tsx (converted from index.js, ST-00259)
//
// ST-00259: the list's load failure toast has a stable id ('telegram-schedules-load'). The list
// reloads on every keystroke in the search box and twice when a filter change also resets the
// page, so a failing endpoint used to queue one identical toast per request; now that the app
// renders toasts (AppToaster) it shows one. The unused router hook and icons were dropped.
import { useState, useEffect, useCallback, type ComponentType } from 'react';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaClock,
  FaHistory,
  FaSearch,
  FaSync,
  FaEye,
  FaChevronRight,
  FaChartLine,
  FaBolt,
  FaChevronLeft,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import type { AxiosResponse } from 'axios';
import api from '../../../../services/api';
import CreateScheduleModalJs from './CreateScheduleModal';
import EditScheduleModalJs from './EditScheduleModal';
import ScheduleHistoryModal from './ScheduleHistoryModal';
import SchedulePreviewModalJs from './SchedulePreviewModal';
import ScheduleAnalyticsModalJs from './ScheduleAnalyticsModal';
import { PageHeader } from '../../../../types/commonComponents';
import type { HttpErrorLike } from '../../../../types/clientShell';
import type {
  TelegramScheduleFormPayload,
  TelegramScheduleListBody,
  TelegramScheduleListParams,
  TelegramScheduleRow,
  TelegramScheduleStatusFilter,
} from '../../../../types/clientPages/telegramSchedules';

// Typed views of the (still JavaScript) schedule modals.
interface ScheduleFormModalProps {
  onClose: () => void;
  onSubmit: (payload: TelegramScheduleFormPayload) => Promise<void>;
}
const CreateScheduleModal =
  CreateScheduleModalJs as unknown as ComponentType<ScheduleFormModalProps>;
const EditScheduleModal = EditScheduleModalJs as unknown as ComponentType<
  ScheduleFormModalProps & { schedule: TelegramScheduleRow }
>;
interface ScheduleViewModalProps {
  schedule: TelegramScheduleRow;
  onClose: () => void;
}
const SchedulePreviewModal =
  SchedulePreviewModalJs as unknown as ComponentType<ScheduleViewModalProps>;
const ScheduleAnalyticsModal =
  ScheduleAnalyticsModalJs as unknown as ComponentType<ScheduleViewModalProps>;

/** The server's `message` for a failed request, else the fallback (as the JavaScript read it). */
const serverMessage = (error: unknown, fallback: string): string =>
  (error as HttpErrorLike | null | undefined)?.response?.data?.message || fallback;

const STATUS_FILTERS: TelegramScheduleStatusFilter[] = ['all', 'active', 'inactive'];

const ScheduleManager = () => {
  const [schedules, setSchedules] = useState<TelegramScheduleRow[]>([]);
  const [selectedSchedule, setSelectedSchedule] = useState<TelegramScheduleRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);

  // Filtering and search
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<TelegramScheduleStatusFilter>('all');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 15;

  // Toggling states (track which schedule IDs are currently toggling)
  const [togglingIds, setTogglingIds] = useState<Set<number>>(new Set());

  const loadSchedules = useCallback(async () => {
    try {
      setLoading(true);
      const params: TelegramScheduleListParams = {
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm || undefined,
        status: filterStatus !== 'all' ? filterStatus : undefined,
      };

      const response = (await api.telegramBI.schedules.getAll(
        params
      )) as AxiosResponse<TelegramScheduleListBody | null>;

      let schedulesData: TelegramScheduleRow[] = [];
      let total = 0;

      const body = response.data;
      if (body) {
        if (Array.isArray(body)) {
          schedulesData = body;
          total = body.length;
        } else if ('data' in body && body.data) {
          schedulesData = body.data.schedules || [];
          total = body.data.totalCount || 0;
        } else if ('schedules' in body && body.schedules) {
          schedulesData = body.schedules || [];
          total = body.totalCount || 0;
        }
      }

      setSchedules(schedulesData);
      setTotalCount(total);
      setTotalPages(Math.ceil(total / itemsPerPage) || 1);
    } catch (error) {
      console.error('Error loading schedules:', error);
      // A stable id: one toast however many reloads fail (ST-00259).
      toast.error(serverMessage(error, 'Failed to load schedules'), {
        toastId: 'telegram-schedules-load',
      });
      setSchedules([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, filterStatus]);

  useEffect(() => {
    loadSchedules();
  }, [loadSchedules]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus]);

  const handleCreateSchedule = async (scheduleData: TelegramScheduleFormPayload) => {
    try {
      await api.telegramBI.schedules.create(scheduleData);
      toast.success('Schedule created successfully');
      setShowCreateModal(false);
      loadSchedules();
    } catch (error) {
      console.error('Error creating schedule:', error);
      toast.error(serverMessage(error, 'Failed to create schedule'));
    }
  };

  const handleUpdateSchedule = async (scheduleData: TelegramScheduleFormPayload) => {
    try {
      // The edit modal is only open with a schedule selected (the JS read it unguarded).
      await api.telegramBI.schedules.update(selectedSchedule!.schedule_id, scheduleData);
      toast.success('Schedule updated successfully');
      setShowEditModal(false);
      setSelectedSchedule(null);
      loadSchedules();
    } catch (error) {
      console.error('Error updating schedule:', error);
      toast.error(serverMessage(error, 'Failed to update schedule'));
    }
  };

  const handleDeleteSchedule = async (scheduleId: number) => {
    if (
      !window.confirm(
        'Are you sure you want to delete this schedule? This action cannot be undone.'
      )
    ) {
      return;
    }
    try {
      await api.telegramBI.schedules.delete(scheduleId);
      toast.success('Schedule deleted successfully');
      if (selectedSchedule?.schedule_id === scheduleId) {
        setSelectedSchedule(null);
      }
      loadSchedules();
    } catch (error) {
      console.error('Error deleting schedule:', error);
      toast.error(serverMessage(error, 'Failed to delete schedule'));
    }
  };

  const handleToggleSchedule = async (schedule: TelegramScheduleRow) => {
    const id = schedule.schedule_id;
    setTogglingIds((prev) => new Set(prev).add(id));
    try {
      await api.telegramBI.schedules.toggleStatus(id, !schedule.is_active);
      toast.success(`Schedule ${!schedule.is_active ? 'activated' : 'paused'} successfully`);
      loadSchedules();
    } catch (error) {
      console.error('Error toggling schedule:', error);
      toast.error(serverMessage(error, 'Failed to toggle schedule status'));
    } finally {
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleExecuteSchedule = async (scheduleId: number) => {
    try {
      await api.telegramBI.schedules.execute(scheduleId);
      toast.success('Schedule executed successfully');
      loadSchedules();
    } catch (error) {
      console.error('Error executing schedule:', error);
      toast.error(serverMessage(error, 'Failed to execute schedule'));
    }
  };

  const formatRelativeTime = (dateString: string | null | undefined) => {
    if (!dateString) return 'Not scheduled';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();

    if (diffMs < 0) {
      const absDiff = Math.abs(diffMs);
      const mins = Math.floor(absDiff / 60000);
      const hours = Math.floor(mins / 60);
      const days = Math.floor(hours / 24);
      if (days > 0) return `${days}d ago`;
      if (hours > 0) return `${hours}h ago`;
      if (mins > 0) return `${mins}m ago`;
      return 'just now';
    }

    const mins = Math.floor(diffMs / 60000);
    const hours = Math.floor(mins / 60);
    const days = Math.floor(hours / 24);
    if (days > 0) return `in ${days} day${days > 1 ? 's' : ''}`;
    if (hours > 0) return `in ${hours} hour${hours > 1 ? 's' : ''}`;
    if (mins > 0) return `in ${mins} min${mins > 1 ? 's' : ''}`;
    return 'in <1 min';
  };

  const formatDateTime = (dateString: string | null | undefined) => {
    if (!dateString) return '--';
    return new Date(dateString).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getScheduleTypeLabel = (type: string | null | undefined) => {
    switch (type) {
      case 'cron':
        return 'Cron';
      case 'recurring':
        return 'Interval';
      case 'once':
        return 'One-time';
      default:
        return type || 'Unknown';
    }
  };

  const getScheduleTypeBadgeClass = (type: string | null | undefined) => {
    switch (type) {
      case 'cron':
        return 'bg-purple-50 text-purple-700 border border-purple-200';
      case 'recurring':
        return 'bg-blue-50 text-blue-700 border border-blue-200';
      case 'once':
        return 'bg-gray-50 text-gray-700 border border-gray-200';
      default:
        return 'bg-gray-50 text-gray-600 border border-gray-200';
    }
  };

  const filteredSchedules = schedules.filter((schedule) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (schedule.schedule_name || '').toLowerCase().includes(term) ||
      (schedule.query_name || '').toLowerCase().includes(term) ||
      (schedule.template_name || '').toLowerCase().includes(term)
    );
  });

  return (
    <div className="min-h-screen bg-slate-50">
      <PageHeader
        icon={FaClock}
        title="Schedules"
        subtitle="Manage automated report delivery schedules"
        actions={
          <button
            type="button"
            onClick={() => setShowCreateModal(true)}
            aria-label="Create Schedule"
            title="Create Schedule"
            className="inline-flex items-center justify-center gap-2 h-10 px-3.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium whitespace-nowrap transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
          >
            <FaPlus className="w-3.5 h-3.5" aria-hidden="true" />
            <span>
              Create<span className="page-header__label"> Schedule</span>
            </span>
          </button>
        }
      />

      <div className="p-3 sm:p-6 max-w-[1800px] mx-auto space-y-6">
        {/* Filter Bar */}
        <div className="bg-surface border border-gray-200 rounded-lg p-4">
          <div className="flex flex-col sm:flex-row items-center gap-3">
            {/* Search */}
            <div className="relative flex-1 w-full">
              <FaSearch
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                size={13}
              />
              <input
                type="text"
                placeholder="Search schedules..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            {/* Status Filter */}
            <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden text-sm">
              {STATUS_FILTERS.map((status) => (
                <button
                  key={status}
                  onClick={() => setFilterStatus(status)}
                  className={`px-3 py-2 font-medium capitalize transition-colors ${
                    filterStatus === status
                      ? 'bg-primary-500 text-white'
                      : 'bg-surface text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {status === 'all' ? 'All' : status}
                </button>
              ))}
            </div>

            {/* Refresh */}
            <button
              onClick={loadSchedules}
              disabled={loading}
              className="flex items-center space-x-1.5 px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <FaSync size={12} className={loading ? 'animate-spin' : ''} />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {/* Schedule Table */}
        <div className="bg-surface border border-gray-200 rounded-lg overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <FaSync className="animate-spin h-6 w-6 text-primary-500 mx-auto mb-3" />
                <p className="text-sm text-gray-500">Loading schedules...</p>
              </div>
            </div>
          ) : filteredSchedules.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <FaClock className="h-10 w-10 text-gray-300 mb-3" />
              <p className="text-gray-500 font-medium">No schedules found</p>
              <p className="text-sm text-gray-400 mt-1 mb-4">
                {searchTerm || filterStatus !== 'all'
                  ? 'Try adjusting your search or filters'
                  : 'Get started by creating your first schedule'}
              </p>
              {!searchTerm && filterStatus === 'all' && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="text-sm text-primary-500 hover:text-primary-600 font-medium"
                >
                  Create your first schedule
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Schedule
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Next Run
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Run
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Query
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Template
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-surface divide-y divide-gray-100">
                  {filteredSchedules.map((schedule) => {
                    const isToggling = togglingIds.has(schedule.schedule_id);
                    const isActive = schedule.is_active;
                    const nextRunOverdue =
                      schedule.next_run_time && new Date(schedule.next_run_time) < new Date();

                    return (
                      <tr key={schedule.schedule_id} className="hover:bg-gray-50 transition-colors">
                        {/* Name */}
                        <td className="px-4 py-3">
                          <div
                            className="text-sm font-medium text-gray-900 truncate max-w-[200px]"
                            title={schedule.schedule_name}
                          >
                            {schedule.schedule_name}
                          </div>
                        </td>

                        {/* Status Toggle */}
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleToggleSchedule(schedule)}
                            disabled={isToggling}
                            className="flex items-center space-x-2 group"
                            title={isActive ? 'Click to deactivate' : 'Click to activate'}
                          >
                            <div
                              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                                isToggling ? 'opacity-50' : ''
                              } ${isActive ? 'bg-green-500' : 'bg-gray-300'}`}
                            >
                              <span
                                className={`inline-block h-3.5 w-3.5 transform rounded-full bg-surface transition-transform shadow-sm ${
                                  isActive ? 'translate-x-4' : 'translate-x-1'
                                }`}
                              />
                            </div>
                            <span
                              className={`text-xs font-medium ${isActive ? 'text-green-700' : 'text-gray-500'}`}
                            >
                              {isActive ? 'Active' : 'Inactive'}
                            </span>
                          </button>
                        </td>

                        {/* Type */}
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getScheduleTypeBadgeClass(schedule.schedule_type)}`}
                          >
                            {getScheduleTypeLabel(schedule.schedule_type)}
                          </span>
                        </td>

                        {/* Next Run */}
                        <td className="px-4 py-3">
                          <div className="text-sm">
                            <span
                              className={`font-medium ${nextRunOverdue && isActive ? 'text-red-600' : 'text-gray-700'}`}
                            >
                              {isActive ? formatRelativeTime(schedule.next_run_time) : '--'}
                            </span>
                            {schedule.next_run_time && isActive && (
                              <div className="text-xs text-gray-400 mt-0.5">
                                {formatDateTime(schedule.next_run_time)}
                              </div>
                            )}
                          </div>
                        </td>

                        {/* Last Run */}
                        <td className="px-4 py-3">
                          <div className="text-sm text-gray-600">
                            {schedule.last_run_time ? formatDateTime(schedule.last_run_time) : '--'}
                          </div>
                        </td>

                        {/* Query */}
                        <td className="px-4 py-3">
                          <div
                            className="text-sm text-gray-700 truncate max-w-[140px]"
                            title={schedule.query_name ?? undefined}
                          >
                            {schedule.query_name || '--'}
                          </div>
                        </td>

                        {/* Template */}
                        <td className="px-4 py-3">
                          <div
                            className="text-sm text-gray-700 truncate max-w-[140px]"
                            title={schedule.template_name ?? undefined}
                          >
                            {schedule.template_name || '--'}
                          </div>
                        </td>

                        {/* Actions */}
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end space-x-1">
                            <button
                              onClick={() => {
                                setSelectedSchedule(schedule);
                                setShowEditModal(true);
                              }}
                              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                              title="Edit"
                            >
                              <FaEdit size={13} />
                            </button>
                            <button
                              onClick={() => handleDeleteSchedule(schedule.schedule_id)}
                              className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Delete"
                            >
                              <FaTrash size={13} />
                            </button>
                            <button
                              onClick={() => handleExecuteSchedule(schedule.schedule_id)}
                              className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                              title="Execute Now"
                            >
                              <FaBolt size={13} />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedSchedule(schedule);
                                setShowHistoryModal(true);
                              }}
                              className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                              title="View History"
                            >
                              <FaHistory size={13} />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedSchedule(schedule);
                                setShowPreviewModal(true);
                              }}
                              className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                              title="Preview"
                            >
                              <FaEye size={13} />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedSchedule(schedule);
                                setShowAnalyticsModal(true);
                              }}
                              className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                              title="View Analytics"
                            >
                              <FaChartLine size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
              <div className="text-xs text-gray-500">
                Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
                {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} schedules
              </div>
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="flex items-center px-2 py-1.5 text-xs border border-gray-300 rounded disabled:opacity-40 hover:bg-surface transition-colors"
                >
                  <FaChevronLeft size={10} className="mr-1" />
                  Previous
                </button>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let page: number;
                  if (totalPages <= 5) {
                    page = i + 1;
                  } else if (currentPage <= 3) {
                    page = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    page = totalPages - 4 + i;
                  } else {
                    page = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-2.5 py-1.5 text-xs rounded transition-colors ${
                        currentPage === page
                          ? 'bg-primary-500 text-white'
                          : 'border border-gray-300 text-gray-600 hover:bg-surface'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="flex items-center px-2 py-1.5 text-xs border border-gray-300 rounded disabled:opacity-40 hover:bg-surface transition-colors"
                >
                  Next
                  <FaChevronRight size={10} className="ml-1" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Modals */}
        {showCreateModal && (
          <CreateScheduleModal
            onClose={() => setShowCreateModal(false)}
            onSubmit={handleCreateSchedule}
          />
        )}

        {showEditModal && selectedSchedule && (
          <EditScheduleModal
            schedule={selectedSchedule}
            onClose={() => {
              setShowEditModal(false);
              setSelectedSchedule(null);
            }}
            onSubmit={handleUpdateSchedule}
          />
        )}

        {showHistoryModal && selectedSchedule && (
          <ScheduleHistoryModal
            schedule={selectedSchedule}
            onClose={() => {
              setShowHistoryModal(false);
              setSelectedSchedule(null);
            }}
          />
        )}

        {showPreviewModal && selectedSchedule && (
          <SchedulePreviewModal
            schedule={selectedSchedule}
            onClose={() => {
              setShowPreviewModal(false);
              setSelectedSchedule(null);
            }}
          />
        )}

        {showAnalyticsModal && selectedSchedule && (
          <ScheduleAnalyticsModal
            schedule={selectedSchedule}
            onClose={() => {
              setShowAnalyticsModal(false);
              setSelectedSchedule(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default ScheduleManager;
